import React, { useContext, useReducer, useCallback } from 'react';

export class UnknownGlobalState extends Error {
}

export function createGlobalState<T, A = T>(reducer: (v: T, a: A) => T, initVal: T) {
    type Value = {
        state: T;
        dispatch: (a: A) => void;
    }

    const Context = React.createContext<Value | undefined>(undefined);

    function useWithState(init: (initVal: T) => T = v => v) {
        const [state, dispatch] = useReducer(reducer, initVal, init);

        return useCallback((children: React.ReactNode) => (
            <Context.Provider value={{ state, dispatch }}>
                {children}
            </Context.Provider >
        ), [state]);
    }

    function useDispatch() {
        const entry = useContext(Context);
        if (!entry) throw new UnknownGlobalState();
        const { dispatch } = entry;
        return dispatch;
    }

    function useState() {
        const entry = useContext(Context);
        if (!entry) throw new UnknownGlobalState();
        const { state } = entry;
        return state;
    }

    return { useWithState, useDispatch, useState };
}
